import { vysionTrack } from '../vysionService';
import { VYSION_ACTIONS } from '..';

export enum PrivacyVysionEventsEnum {
  GPC_DETECTED = 'GPC_DETECTED',
  ONBOARDING_COOKIE_CONSENT_SHOWN = 'ONBOARDING_COOKIE_CONSENT_SHOWN',
  EDITPROFILE_COOKIE_CONSENT_SHOWN = 'EDITPROFILE_COOKIE_CONSENT_SHOWN',
  CONSENT_DECISION = 'CONSENT_DECISION',
}

type PrivacyEventDetails = {
  [key in PrivacyVysionEventsEnum]: {
    action: VYSION_ACTIONS;
    page: string;
    eventName: string;
    component?: string;
    extraInfo?: string;
    path?: string;
  };
};

const privacyEvents: PrivacyEventDetails = {
  [PrivacyVysionEventsEnum.GPC_DETECTED]: {
    action: VYSION_ACTIONS.VIEW,
    page: 'Join Page',
    eventName: 'gpc-detected',
    component: 'Location setting',
    path: '/join',
    extraInfo: '',
  },
  [PrivacyVysionEventsEnum.ONBOARDING_COOKIE_CONSENT_SHOWN]: {
    action: VYSION_ACTIONS.VIEW,
    page: 'Join Page',
    eventName: 'onboarding-cookie-consent-shown',
    component: 'Location setting',
    path: '/join',
    extraInfo: '',
  },
  [PrivacyVysionEventsEnum.EDITPROFILE_COOKIE_CONSENT_SHOWN]: {
    action: VYSION_ACTIONS.VIEW,
    page: 'Edit My Profile',
    eventName: 'editprofile-cookie-consent-shown',
    component: 'Location setting',
    path: '/profile/edit',
    extraInfo: '',
  },
  [PrivacyVysionEventsEnum.CONSENT_DECISION]: {
    action: VYSION_ACTIONS.CLICK,
    page: 'Join Page', // Can be "Join Page" or "Edit My Profile"
    eventName: 'consent-decision',
    component: 'Cookie Consent Modal',
    path: '/join', // Can be "/join" or "/profile/edit"
    extraInfo: '',
  },
};

interface GpcDetectedPayload {
  userAgent?: string;
  platform?: string;
  language?: string;
  gpcSupported?: boolean;
  gpcActive?: boolean;
  preferenceState: 'deny';
}

/**
 * Track GPC detection event
 *
 * IMPORTANT: This event is for compliance monitoring only.
 * It MUST NOT be used for:
 * - User profiling
 * - Behavioral advertising
 * - Marketing purposes
 * - Personalization targeting
 *
 * This event tracks when a Global Privacy Control signal is detected
 * and the user's preference state is automatically set to "deny".
 *
 * @param metadata - Browser/device metadata and preference state
 */
export const trackGpcDetected = (metadata: GpcDetectedPayload): void => {
  const eventConfig = privacyEvents[PrivacyVysionEventsEnum.GPC_DETECTED];
  const { eventName, ...rest } = eventConfig;

  vysionTrack(
    {
      eventData: {
        ...rest,
        // Include browser/device metadata
        ...metadata,
      },
    },
    eventName
  );
};

/**
 * Track onboarding cookie consent modal shown event
 *
 * IMPORTANT: This event is for compliance monitoring only.
 * It MUST NOT be used for:
 * - User profiling
 * - Behavioral advertising
 * - Marketing purposes
 * - Personalization targeting
 *
 * This event tracks when the cookie consent modal is shown during
 * the onboarding flow on the /join page.
 */
export const trackOnboardingCookieConsentShown = (): void => {
  const eventConfig = privacyEvents[PrivacyVysionEventsEnum.ONBOARDING_COOKIE_CONSENT_SHOWN];
  const { eventName, ...rest } = eventConfig;

  vysionTrack(
    {
      eventData: {
        ...rest,
      },
    },
    eventName
  );
};

/**
 * Track edit profile cookie consent modal shown event
 *
 * IMPORTANT: This event is for compliance monitoring only.
 * It MUST NOT be used for:
 * - User profiling
 * - Behavioral advertising
 * - Marketing purposes
 * - Personalization targeting
 *
 * This event tracks when the cookie consent modal is shown after
 * a user changes their location on the profile edit page.
 */
export const trackEditProfileCookieConsentShown = (): void => {
  const eventConfig = privacyEvents[PrivacyVysionEventsEnum.EDITPROFILE_COOKIE_CONSENT_SHOWN];
  const { eventName, ...rest } = eventConfig;

  vysionTrack(
    {
      eventData: {
        ...rest,
      },
    },
    eventName
  );
};

/**
 * Track user consent decision
 *
 * IMPORTANT: This event is for compliance monitoring only.
 * It MUST NOT be used for:
 * - User profiling
 * - Behavioral advertising
 * - Marketing purposes
 * - Personalization targeting
 *
 * This event tracks when a user makes a decision on the cookie consent modal.
 *
 * @param decision - The user's decision: "Allow all", "Deny all", or "Closed (No Decision)"
 * @param page - The page where the decision was made: "Join Page" or "Edit My Profile"
 * @param path - The path where the decision was made: "/join" or "/profile/edit"
 */
export const trackConsentDecision = (decision: string, page: string = 'Join Page', path?: string): void => {
  const eventConfig = privacyEvents[PrivacyVysionEventsEnum.CONSENT_DECISION];

  // Determine path from page if not provided
  const eventPath = path || (page === 'Edit My Profile' ? '/profile/edit' : '/join');

  vysionTrack(
    {
      eventData: {
        page, // Use the dynamic page parameter
        component: eventConfig.component,
        action: eventConfig.action,
        decision,
        path: eventPath,
        extraInfo: eventConfig.extraInfo,
      },
    },
    eventConfig.eventName
  );
};

/**
 * Handle privacy-related vysion events
 *
 * @param event - The privacy event to track
 * @param metadata - Additional event metadata
 */
export const handlePrivacyVysionEvent = (event: PrivacyVysionEventsEnum, metadata?: Record<string, unknown>): void => {
  const eventConfig = privacyEvents[event];

  if (!eventConfig) {
    return;
  }

  vysionTrack(
    {
      eventData: {
        page: eventConfig.page,
        component: eventConfig.component,
        action: eventConfig.action,
        ...metadata,
      },
    },
    eventConfig.eventName
  );
};
